import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sspAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  FileText,
  Loader2,
  Save,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Circle,
  Shield,
  Users,
  Server,
  Network,
  Lock,
  AlertCircle,
  ArrowLeft,
  Settings,
  List,
  Download,
  Clock
} from 'lucide-react';

const SSP_SECTIONS = [
  { id: 'executive', name: 'Executive Summary', icon: FileText },
  { id: 'system', name: 'System Identification', icon: Server },
  { id: 'status', name: 'Operational Status', icon: Settings },
  { id: 'description', name: 'System Description', icon: List },
  { id: 'environment', name: 'System Environment', icon: Server },
  { id: 'interconnections', name: 'Interconnections', icon: Network },
  { id: 'controls', name: 'Security Controls', icon: Lock },
  { id: 'guidance', name: 'Implementation Guidance', icon: Shield },
];

const SSPEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [ssp, setSsp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('executive');
  const [controlsData, setControlsData] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const autoSaveTimerRef = useRef(null);

  useEffect(() => {
    fetchSSP();
  }, [id]);

  // Auto-save every 30 seconds when there are unsaved changes
  useEffect(() => {
    if (unsavedChanges && ssp) {
      autoSaveTimerRef.current = setTimeout(async () => {
        try {
          await sspAPI.update(id, ssp);
          setLastAutoSave(new Date());
          setUnsavedChanges(false);
          notify.info('Auto-saved');
        } catch (err) {
          console.error('Auto-save failed:', err);
        }
      }, 30000); // 30 seconds
    }
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [unsavedChanges, ssp, id, notify]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+S or Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (unsavedChanges && !saving) {
          handleSave();
        }
      }
      
      // Ctrl+Left or Cmd+Left to go to previous section
      if ((e.ctrlKey || e.metaKey) && e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevSection();
      }
      
      // Ctrl+Right or Cmd+Right to go to next section
      if ((e.ctrlKey || e.metaKey) && e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextSection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [unsavedChanges, saving, activeSection]);

  const fetchSSP = async () => {
    try {
      const [sspRes, controlsRes] = await Promise.all([
        sspAPI.get(id),
        sspAPI.getControls(id)
      ]);
      setSsp(sspRes.data);
      setControlsData(controlsRes.data);
    } catch (err) {
      console.error('Failed to fetch SSP:', err);
      navigate('/ssp');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sspAPI.update(id, ssp);
      setSsp(res.data);
      setUnsavedChanges(false);
      setLastAutoSave(new Date());
      notify.success('Saved successfully');
    } catch (err) {
      console.error('Failed to save SSP:', err);
      notify.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSsp(prev => ({ ...prev, [field]: value }));
    setUnsavedChanges(true);
  };

  const updateContactInfo = (type, field, value) => {
    setSsp(prev => ({
      ...prev,
      [type]: { ...(prev[type] || {}), [field]: value }
    }));
    setUnsavedChanges(true);
  };

  const handleControlUpdate = async (controlId, field, value) => {
    const control = controlsData.controls.find(c => c.controlId === controlId);
    if (!control) return;

    const updatedControl = { ...control, [field]: value };

    try {
      await sspAPI.updateControl(id, controlId, updatedControl);
      // Update local state
      setControlsData(prev => ({
        ...prev,
        controls: prev.controls.map(c => c.controlId === controlId ? updatedControl : c),
        groupedControls: Object.fromEntries(
          Object.entries(prev.groupedControls).map(([cat, controls]) => [
            cat,
            controls.map(c => c.controlId === controlId ? updatedControl : c)
          ])
        )
      }));
    } catch (err) {
      console.error('Failed to update control:', err);
    }
  };

  const getCompletedSections = () => {
    if (!ssp) return [];
    const completed = [];
    if (ssp.executiveSummary) completed.push('executive');
    if (ssp.systemName) completed.push('system');
    if (ssp.operationalStatus) completed.push('status');
    if (ssp.generalDescription) completed.push('description');
    if (ssp.environmentDescription) completed.push('environment');
    if (ssp.dataFlowDescription) completed.push('interconnections');
    if (controlsData?.stats?.implemented > 0) completed.push('controls');
    if (ssp.implementationGuidance) completed.push('guidance');
    return completed;
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // ==================== COVER PAGE ====================
      // Full page gradient background
      doc.setFillColor(30, 41, 59);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Purple accent stripe at top
      doc.setFillColor(139, 92, 246);
      doc.rect(0, 0, pageWidth, 8, 'F');
      
      // Aspiritech branding
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(32);
      doc.text('Aspiritech', pageWidth / 2, 50, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setTextColor(200, 180, 255);
      doc.text('Cybersecurity', pageWidth / 2, 62, { align: 'center' });
      
      // Main title
      doc.setFontSize(28);
      doc.setTextColor(255, 255, 255);
      doc.text('System Security Plan', pageWidth / 2, 100, { align: 'center' });
      
      // Document name
      doc.setFontSize(18);
      doc.setTextColor(139, 92, 246);
      doc.text(ssp?.name || 'SSP Document', pageWidth / 2, 120, { align: 'center' });
      
      // Tagline box
      doc.setFillColor(88, 28, 135);
      doc.roundedRect(40, 145, pageWidth - 80, 30, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text('Masks Down. Shields Up.', pageWidth / 2, 163, { align: 'center' });
      
      // Status and completion info
      doc.setFontSize(12);
      doc.setTextColor(200, 200, 220);
      doc.text(`Status: ${ssp?.status || 'Draft'}`, pageWidth / 2, 200, { align: 'center' });
      doc.text(`Completion: ${ssp?.completionPercentage || 0}%`, pageWidth / 2, 215, { align: 'center' });
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 230, { align: 'center' });
      
      // Footer text on cover
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 150);
      doc.text('NIST 800-171 Compliant Security Documentation', pageWidth / 2, pageHeight - 30, { align: 'center' });
      doc.text('Neurodivergent minds see what others miss.', pageWidth / 2, pageHeight - 20, { align: 'center' });
      
      // ==================== TABLE OF CONTENTS ====================
      doc.addPage();
      addPageHeader(doc, pageWidth, 'Table of Contents');
      
      let yPos = 60;
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      
      const tocItems = [
        { num: '1', title: 'Executive Summary', page: 3 },
        { num: '2', title: 'System Identification', page: 4 },
        { num: '3', title: 'Operational Status', page: 5 },
        { num: '4', title: 'General System Description', page: 6 },
        { num: '5', title: 'System Environment', page: 7 },
        { num: '6', title: 'System Interconnections', page: 8 },
        { num: '7', title: 'Security Controls (NIST 800-171)', page: 9 },
        { num: '8', title: 'Implementation Guidance', page: 10 },
      ];
      
      tocItems.forEach((item) => {
        doc.setFont(undefined, 'bold');
        doc.text(`${item.num}.`, 25, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(item.title, 35, yPos);
        doc.text(`.......................................`, 100, yPos);
        yPos += 12;
      });

      // ==================== SECTION 1: EXECUTIVE SUMMARY ====================
      doc.addPage();
      addPageHeader(doc, pageWidth, '1. Executive Summary');
      yPos = 60;
      
      if (ssp?.executiveSummary) {
        doc.setFontSize(11);
        doc.setTextColor(50, 50, 50);
        const execLines = doc.splitTextToSize(ssp.executiveSummary, pageWidth - 50);
        doc.text(execLines, 25, yPos);
      } else {
        doc.setTextColor(128, 128, 128);
        doc.setFontSize(11);
        doc.text('No executive summary provided.', 25, yPos);
      }

      // ==================== SECTION 2: SYSTEM IDENTIFICATION ====================
      doc.addPage();
      addPageHeader(doc, pageWidth, '2. System Identification');
      yPos = 60;
      
      const sysIdData = [
        ['System Name', ssp?.systemName || 'Not specified'],
        ['System Type', ssp?.systemType || 'Not specified'],
        ['System Information Type', ssp?.systemInfoType || 'Not specified'],
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [['Field', 'Value']],
        body: sysIdData,
        theme: 'striped',
        headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255] },
        bodyStyles: { textColor: [30, 41, 59] },
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
      
      // System Owner Contact
      if (ssp?.infoSystemOwner) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.text('Information System Owner', 25, yPos);
        yPos += 8;
        
        const ownerData = [
          ['Name', ssp.infoSystemOwner.name || ''],
          ['Title', ssp.infoSystemOwner.title || ''],
          ['Department', ssp.infoSystemOwner.department || ''],
          ['Phone', ssp.infoSystemOwner.phone || ''],
          ['Email', ssp.infoSystemOwner.email || ''],
        ].filter(row => row[1]);
        
        if (ownerData.length > 0) {
          autoTable(doc, {
            startY: yPos,
            body: ownerData,
            theme: 'plain',
            bodyStyles: { textColor: [30, 41, 59], fontSize: 10 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 } },
          });
          yPos = doc.lastAutoTable.finalY + 10;
        }
      }
      
      // System Management Contact
      if (ssp?.infoSystemManagement) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 92, 246);
        doc.text('Information System Management (IT Staff)', 25, yPos);
        yPos += 8;
        
        const mgmtData = [
          ['Name', ssp.infoSystemManagement.name || ''],
          ['Title', ssp.infoSystemManagement.title || ''],
          ['Department', ssp.infoSystemManagement.department || ''],
          ['Phone', ssp.infoSystemManagement.phone || ''],
          ['Email', ssp.infoSystemManagement.email || ''],
        ].filter(row => row[1]);
        
        if (mgmtData.length > 0) {
          autoTable(doc, {
            startY: yPos,
            body: mgmtData,
            theme: 'plain',
            bodyStyles: { textColor: [30, 41, 59], fontSize: 10 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 } },
          });
        }
      }

      // ==================== SECTION 3: OPERATIONAL STATUS ====================
      doc.addPage();
      addPageHeader(doc, pageWidth, '3. Operational Status');
      yPos = 60;
      
      const statusData = [
        ['Overall Status', ssp?.operationalStatus || 'Not specified'],
      ];
      
      autoTable(doc, {
        startY: yPos,
        body: statusData,
        theme: 'plain',
        bodyStyles: { textColor: [30, 41, 59] },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } },
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
      
      if (ssp?.operationalParts) {
        addSubsection(doc, 'Operational Parts', ssp.operationalParts, yPos, pageWidth);
        yPos += 30 + doc.splitTextToSize(ssp.operationalParts, pageWidth - 50).length * 5;
      }
      
      if (ssp?.underDevelopmentParts) {
        addSubsection(doc, 'Under Development Parts', ssp.underDevelopmentParts, yPos, pageWidth);
        yPos += 30 + doc.splitTextToSize(ssp.underDevelopmentParts, pageWidth - 50).length * 5;
      }
      
      if (ssp?.developmentTimeline) {
        addSubsection(doc, 'Development Timeline', ssp.developmentTimeline, yPos, pageWidth);
      }

      // ==================== SECTION 4: SYSTEM DESCRIPTION ====================
      doc.addPage();
      addPageHeader(doc, pageWidth, '4. General System Description');
      yPos = 60;
      
      if (ssp?.generalDescription) {
        const descLines = doc.splitTextToSize(ssp.generalDescription, pageWidth - 50);
        doc.setFontSize(11);
        doc.setTextColor(50, 50, 50);
        doc.text(descLines, 25, yPos);
        yPos += descLines.length * 5 + 15;
      }
      
      if (ssp?.useCases) {
        addSubsection(doc, 'Use Cases / User Stories', ssp.useCases, yPos, pageWidth);
        yPos += 30 + doc.splitTextToSize(ssp.useCases, pageWidth - 50).length * 5;
      }
      
      if (ssp?.dataInputsOutputs) {
        addSubsection(doc, 'Data Inputs and Outputs', ssp.dataInputsOutputs, yPos, pageWidth);
        yPos += 30 + doc.splitTextToSize(ssp.dataInputsOutputs, pageWidth - 50).length * 5;
      }
      
      if (ssp?.dataCollectionStorage) {
        addSubsection(doc, 'Data Collection and Storage', ssp.dataCollectionStorage, yPos, pageWidth);
      }

      // ==================== SECTION 5: SYSTEM ENVIRONMENT ====================
      doc.addPage();
      addPageHeader(doc, pageWidth, '5. System Environment');
      yPos = 60;
      
      const envData = [
        ['Physical Location', ssp?.physicalLocation || ''],
        ['Hosting Organization', ssp?.hostingOrganization || ''],
        ['Operating System', ssp?.operatingSystem || ''],
        ['Make and Model', ssp?.makeModel || ''],
        ['Anti-Virus', ssp?.antiVirus || ''],
        ['Firewalls', ssp?.firewalls || ''],
        ['DMZ', ssp?.dmz || ''],
      ].filter(row => row[1]);
      
      if (envData.length > 0) {
        autoTable(doc, {
          startY: yPos,
          head: [['Component', 'Details']],
          body: envData,
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255] },
          bodyStyles: { textColor: [30, 41, 59] },
        });
        yPos = doc.lastAutoTable.finalY + 15;
      }
      
      if (ssp?.environmentDescription) {
        addSubsection(doc, 'Environment Description', ssp.environmentDescription, yPos, pageWidth);
        yPos += 30 + doc.splitTextToSize(ssp.environmentDescription, pageWidth - 50).length * 5;
      }
      
      if (ssp?.licensedSoftware) {
        addSubsection(doc, 'Licensed Software', ssp.licensedSoftware, yPos, pageWidth);
      }

      // ==================== SECTION 6: INTERCONNECTIONS ====================
      doc.addPage();
      addPageHeader(doc, pageWidth, '6. System Interconnections');
      yPos = 60;
      
      if (ssp?.dataFlowDescription) {
        addSubsection(doc, 'Data Flow Description', ssp.dataFlowDescription, yPos, pageWidth);
        yPos += 30 + doc.splitTextToSize(ssp.dataFlowDescription, pageWidth - 50).length * 5;
      }
      
      if (ssp?.entryExitProtections) {
        addSubsection(doc, 'Entry/Exit Protections', ssp.entryExitProtections, yPos, pageWidth);
      }

      // ==================== SECTION 7: SECURITY CONTROLS ====================
      doc.addPage();
      addPageHeader(doc, pageWidth, '7. Security Controls (NIST 800-171)');
      yPos = 60;
      
      // Control statistics
      if (controlsData?.stats) {
        const stats = controlsData.stats;
        const statsData = [
          ['Implemented', stats.implemented?.toString() || '0', [34, 197, 94]],
          ['Partially Implemented', stats.partiallyImplemented?.toString() || '0', [234, 179, 8]],
          ['Planned', stats.planned?.toString() || '0', [59, 130, 246]],
          ['Not Applicable', stats.notApplicable?.toString() || '0', [128, 128, 128]],
          ['Total Controls', stats.total?.toString() || '0', [30, 41, 59]],
        ];
        
        autoTable(doc, {
          startY: yPos,
          head: [['Status', 'Count']],
          body: statsData.map(row => [row[0], row[1]]),
          theme: 'grid',
          headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255] },
          bodyStyles: { textColor: [30, 41, 59] },
          columnStyles: { 1: { halign: 'center' } },
        });
        yPos = doc.lastAutoTable.finalY + 15;
      }
      
      // Controls by category
      if (controlsData?.groupedControls) {
        Object.entries(controlsData.groupedControls).forEach(([category, controls]) => {
          // Check if we need a new page
          if (yPos > pageHeight - 60) {
            doc.addPage();
            addPageHeader(doc, pageWidth, '7. Security Controls (continued)');
            yPos = 60;
          }
          
          doc.setFontSize(11);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(139, 92, 246);
          doc.text(category, 25, yPos);
          yPos += 8;
          
          const controlTableData = controls.map(c => [
            c.controlId,
            c.controlTitle.length > 50 ? c.controlTitle.substring(0, 47) + '...' : c.controlTitle,
            c.implementationStatus || 'Not Applicable',
          ]);
          
          autoTable(doc, {
            startY: yPos,
            head: [['ID', 'Control', 'Status']],
            body: controlTableData,
            theme: 'striped',
            headStyles: { fillColor: [71, 85, 105], textColor: [255, 255, 255], fontSize: 9 },
            bodyStyles: { textColor: [30, 41, 59], fontSize: 8 },
            columnStyles: {
              0: { cellWidth: 25 },
              1: { cellWidth: 'auto' },
              2: { cellWidth: 35 },
            },
            didParseCell: function(data) {
              if (data.column.index === 2 && data.section === 'body') {
                const status = data.cell.text[0];
                if (status === 'Implemented') {
                  data.cell.styles.textColor = [34, 197, 94];
                } else if (status === 'Partially implemented') {
                  data.cell.styles.textColor = [234, 179, 8];
                } else if (status === 'Planned') {
                  data.cell.styles.textColor = [59, 130, 246];
                }
              }
            },
          });
          yPos = doc.lastAutoTable.finalY + 10;
        });
      }

      // ==================== SECTION 8: IMPLEMENTATION GUIDANCE ====================
      doc.addPage();
      addPageHeader(doc, pageWidth, '8. Implementation Guidance');
      yPos = 60;
      
      if (ssp?.implementationGuidance) {
        const guidanceLines = doc.splitTextToSize(ssp.implementationGuidance, pageWidth - 50);
        doc.setFontSize(11);
        doc.setTextColor(50, 50, 50);
        doc.text(guidanceLines, 25, yPos);
        yPos += guidanceLines.length * 5 + 15;
      }
      
      if (ssp?.poamMilestones) {
        addSubsection(doc, 'POA&M Milestones', ssp.poamMilestones, yPos, pageWidth);
        yPos += 30 + doc.splitTextToSize(ssp.poamMilestones, pageWidth - 50).length * 5;
      }
      
      if (ssp?.acronyms) {
        addSubsection(doc, 'Acronyms', ssp.acronyms, yPos, pageWidth);
      }

      // ==================== ADD FOOTERS TO ALL PAGES ====================
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 2; i <= pageCount; i++) { // Skip cover page
        doc.setPage(i);
        
        // Footer bar
        doc.setFillColor(248, 250, 252);
        doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
        
        // Purple accent line
        doc.setFillColor(139, 92, 246);
        doc.rect(0, pageHeight - 15, pageWidth, 1, 'F');
        
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Aspiritech SSP • ${ssp?.name || 'System Security Plan'} • Masks Down, Shields Up`,
          20,
          pageHeight - 6
        );
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth - 30,
          pageHeight - 6
        );
      }
      
      doc.save(`${ssp?.name || 'SSP'}-System-Security-Plan.pdf`);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    } finally {
      setGenerating(false);
    }
  };
  
  // Helper function to add page headers
  const addPageHeader = (doc, pageWidth, title) => {
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setFillColor(139, 92, 246);
    doc.rect(0, 40, pageWidth, 3, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text('Aspiritech', 20, 15);
    doc.setFontSize(8);
    doc.setTextColor(200, 180, 255);
    doc.text('Cybersecurity', 20, 22);
    
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text(title, 20, 34);
  };
  
  // Helper function to add subsections
  const addSubsection = (doc, title, content, yPos, pageWidth) => {
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(139, 92, 246);
    doc.text(title, 25, yPos);
    
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    const lines = doc.splitTextToSize(content, pageWidth - 50);
    doc.text(lines, 25, yPos + 8);
  };

  const goToNextSection = () => {
    const currentIdx = SSP_SECTIONS.findIndex(s => s.id === activeSection);
    if (currentIdx < SSP_SECTIONS.length - 1) {
      setActiveSection(SSP_SECTIONS[currentIdx + 1].id);
    }
  };

  const goToPrevSection = () => {
    const currentIdx = SSP_SECTIONS.findIndex(s => s.id === activeSection);
    if (currentIdx > 0) {
      setActiveSection(SSP_SECTIONS[currentIdx - 1].id);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-purple-500 animate-spin" />
      </div>
    );
  }

  const completedSections = getCompletedSections();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-16">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 sticky top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/ssp')}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">{ssp?.name}</h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    ssp?.status === 'Complete' ? 'bg-green-500/20 text-green-400' :
                    ssp?.status === 'In Progress' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-slate-500/20 text-slate-400'
                  }`}>
                    {ssp?.status}
                  </span>
                  <span className="text-slate-400 text-sm">{ssp?.completionPercentage}% Complete</span>
                  {lastAutoSave && (
                    <span className="flex items-center gap-1 text-slate-500 text-xs">
                      <Clock className="w-3 h-3" />
                      Auto-saved {lastAutoSave.toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSave}
                disabled={saving || !unsavedChanges}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
                title="Ctrl+S"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {unsavedChanges ? 'Save Changes' : 'Saved'}
              </button>
              <button
                onClick={generatePDF}
                disabled={generating}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export PDF
                  </>
                )}
              </button>
            </div>
          </div>
          {/* Keyboard shortcuts hint */}
          <div className="mt-2 text-xs text-slate-500">
            Shortcuts: <kbd className="px-1 py-0.5 bg-slate-700 rounded text-slate-400">Ctrl+S</kbd> Save • 
            <kbd className="px-1 py-0.5 bg-slate-700 rounded text-slate-400 ml-1">Ctrl+←</kbd> Previous • 
            <kbd className="px-1 py-0.5 bg-slate-700 rounded text-slate-400 ml-1">Ctrl+→</kbd> Next
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <nav className="w-64 flex-shrink-0">
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 sticky top-40">
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4">Sections</h3>
              <div className="space-y-1">
                {SSP_SECTIONS.map((section) => {
                  const Icon = section.icon;
                  const isComplete = completedSections.includes(section.id);
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        isActive 
                          ? 'bg-purple-600 text-white' 
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {isComplete ? (
                        <CheckCircle className={`w-4 h-4 ${isActive ? 'text-white' : 'text-green-400'}`} />
                      ) : (
                        <Circle className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                      )}
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{section.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-1">
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
              {/* Executive Summary Section */}
              {activeSection === 'executive' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-2">Executive Summary</h2>
                    <p className="text-slate-400 text-sm">
                      Provide a brief summary of work being completed under the contract, including an explanation of research 
                      or work being conducted and an overview of outside organizations involved.
                    </p>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Executive Summary</label>
                    <textarea
                      value={ssp?.executiveSummary || ''}
                      onChange={(e) => updateField('executiveSummary', e.target.value)}
                      rows={10}
                      placeholder="Describe the work being completed, interactions with outside organizations, and roles/responsibilities of personnel..."
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>
                </div>
              )}

              {/* System Identification Section */}
              {activeSection === 'system' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-2">System Identification</h2>
                    <p className="text-slate-400 text-sm">
                      Identify the system name, type, owners, and key contacts.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-300 text-sm mb-2">System Name</label>
                      <input
                        type="text"
                        value={ssp?.systemName || ''}
                        onChange={(e) => updateField('systemName', e.target.value)}
                        placeholder="e.g., Corporate IT Infrastructure"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm mb-2">System Type</label>
                      <select
                        value={ssp?.systemType || ''}
                        onChange={(e) => updateField('systemType', e.target.value)}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Select type...</option>
                        <option value="Major Application">Major Application (database/custom code)</option>
                        <option value="General Support System">General Support System (e.g., Windows AD)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">System Information Type</label>
                    <select
                      value={ssp?.systemInfoType || ''}
                      onChange={(e) => updateField('systemInfoType', e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select type...</option>
                      <option value="Management and Support">Management and Support</option>
                      <option value="Research Focused">Research Focused</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Administrative Users List</label>
                    <textarea
                      value={ssp?.adminUsersList || ''}
                      onChange={(e) => updateField('adminUsersList', e.target.value)}
                      rows={3}
                      placeholder="List individuals with administrative rights to workstations and servers..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  {/* System Owner Contact */}
                  <div className="border border-slate-600 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-400" />
                      Information System Owner
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Name"
                        value={ssp?.infoSystemOwner?.name || ''}
                        onChange={(e) => updateContactInfo('infoSystemOwner', 'name', e.target.value)}
                        className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        placeholder="Title"
                        value={ssp?.infoSystemOwner?.title || ''}
                        onChange={(e) => updateContactInfo('infoSystemOwner', 'title', e.target.value)}
                        className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        placeholder="Department"
                        value={ssp?.infoSystemOwner?.department || ''}
                        onChange={(e) => updateContactInfo('infoSystemOwner', 'department', e.target.value)}
                        className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        placeholder="Phone"
                        value={ssp?.infoSystemOwner?.phone || ''}
                        onChange={(e) => updateContactInfo('infoSystemOwner', 'phone', e.target.value)}
                        className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={ssp?.infoSystemOwner?.email || ''}
                        onChange={(e) => updateContactInfo('infoSystemOwner', 'email', e.target.value)}
                        className="col-span-2 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  {/* System Management Contact */}
                  <div className="border border-slate-600 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5 text-purple-400" />
                      Information System Management (IT Staff)
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Name"
                        value={ssp?.infoSystemManagement?.name || ''}
                        onChange={(e) => updateContactInfo('infoSystemManagement', 'name', e.target.value)}
                        className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        placeholder="Title"
                        value={ssp?.infoSystemManagement?.title || ''}
                        onChange={(e) => updateContactInfo('infoSystemManagement', 'title', e.target.value)}
                        className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        placeholder="Department"
                        value={ssp?.infoSystemManagement?.department || ''}
                        onChange={(e) => updateContactInfo('infoSystemManagement', 'department', e.target.value)}
                        className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        placeholder="Phone"
                        value={ssp?.infoSystemManagement?.phone || ''}
                        onChange={(e) => updateContactInfo('infoSystemManagement', 'phone', e.target.value)}
                        className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={ssp?.infoSystemManagement?.email || ''}
                        onChange={(e) => updateContactInfo('infoSystemManagement', 'email', e.target.value)}
                        className="col-span-2 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Operational Status Section */}
              {activeSection === 'status' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-2">System Operational Status</h2>
                    <p className="text-slate-400 text-sm">
                      What is the current status of the system or parts of the system?
                    </p>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Overall Status</label>
                    <select
                      value={ssp?.operationalStatus || ''}
                      onChange={(e) => updateField('operationalStatus', e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select status...</option>
                      <option value="Operational">Operational - The system is in production</option>
                      <option value="Under Development">Under Development - Being designed/developed</option>
                      <option value="Major Modification">Major Modification - Undergoing major conversion</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Operational Parts</label>
                    <textarea
                      value={ssp?.operationalParts || ''}
                      onChange={(e) => updateField('operationalParts', e.target.value)}
                      rows={3}
                      placeholder="Describe parts of the system that are already operational..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Under Development Parts</label>
                    <textarea
                      value={ssp?.underDevelopmentParts || ''}
                      onChange={(e) => updateField('underDevelopmentParts', e.target.value)}
                      rows={3}
                      placeholder="Describe parts of the system still under development..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Major Modification Parts</label>
                    <textarea
                      value={ssp?.majorModificationParts || ''}
                      onChange={(e) => updateField('majorModificationParts', e.target.value)}
                      rows={3}
                      placeholder="Describe parts undergoing major modifications..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Development Timeline</label>
                    <textarea
                      value={ssp?.developmentTimeline || ''}
                      onChange={(e) => updateField('developmentTimeline', e.target.value)}
                      rows={3}
                      placeholder="If under development, outline major activities and projected timeline..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>
                </div>
              )}

              {/* System Description Section */}
              {activeSection === 'description' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-2">General System Description</h2>
                    <p className="text-slate-400 text-sm">
                      Provide a general description of the system, its scope, major functions, and data handling.
                    </p>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">General Description</label>
                    <textarea
                      value={ssp?.generalDescription || ''}
                      onChange={(e) => updateField('generalDescription', e.target.value)}
                      rows={6}
                      placeholder="Describe the system's role in conducting work, major functions, and architecture overview..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Use Cases / User Stories</label>
                    <textarea
                      value={ssp?.useCases || ''}
                      onChange={(e) => updateField('useCases', e.target.value)}
                      rows={4}
                      placeholder="Describe significant use cases or user stories the system implements..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Data Inputs and Outputs</label>
                    <textarea
                      value={ssp?.dataInputsOutputs || ''}
                      onChange={(e) => updateField('dataInputsOutputs', e.target.value)}
                      rows={4}
                      placeholder="Describe significant data or information inputs and outputs..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Data Collection and Storage</label>
                    <textarea
                      value={ssp?.dataCollectionStorage || ''}
                      onChange={(e) => updateField('dataCollectionStorage', e.target.value)}
                      rows={4}
                      placeholder="Outline what types of data is collected and stored, and which entity controls it..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>
                </div>
              )}

              {/* System Environment Section */}
              {activeSection === 'environment' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-2">System Environment</h2>
                    <p className="text-slate-400 text-sm">
                      Describe the system architecture, including hardware, software, and security components.
                    </p>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Environment Description</label>
                    <textarea
                      value={ssp?.environmentDescription || ''}
                      onChange={(e) => updateField('environmentDescription', e.target.value)}
                      rows={6}
                      placeholder="Describe the system architecture including all major functions..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-300 text-sm mb-2">Physical Location</label>
                      <input
                        type="text"
                        value={ssp?.physicalLocation || ''}
                        onChange={(e) => updateField('physicalLocation', e.target.value)}
                        placeholder="e.g., On-premise data center, AWS us-east-1"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm mb-2">Hosting Organization</label>
                      <input
                        type="text"
                        value={ssp?.hostingOrganization || ''}
                        onChange={(e) => updateField('hostingOrganization', e.target.value)}
                        placeholder="Organization that hosts and manages the system"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm mb-2">Operating System</label>
                      <input
                        type="text"
                        value={ssp?.operatingSystem || ''}
                        onChange={(e) => updateField('operatingSystem', e.target.value)}
                        placeholder="e.g., Windows Server 2022, Ubuntu 22.04"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm mb-2">Make and Model</label>
                      <input
                        type="text"
                        value={ssp?.makeModel || ''}
                        onChange={(e) => updateField('makeModel', e.target.value)}
                        placeholder="Hardware make and model"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Licensed Software</label>
                    <textarea
                      value={ssp?.licensedSoftware || ''}
                      onChange={(e) => updateField('licensedSoftware', e.target.value)}
                      rows={3}
                      placeholder="List licensed software for major functions..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-slate-300 text-sm mb-2">Anti-Virus</label>
                      <input
                        type="text"
                        value={ssp?.antiVirus || ''}
                        onChange={(e) => updateField('antiVirus', e.target.value)}
                        placeholder="e.g., CrowdStrike, Defender"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm mb-2">Firewalls</label>
                      <input
                        type="text"
                        value={ssp?.firewalls || ''}
                        onChange={(e) => updateField('firewalls', e.target.value)}
                        placeholder="e.g., Palo Alto, Fortinet"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm mb-2">DMZ</label>
                      <input
                        type="text"
                        value={ssp?.dmz || ''}
                        onChange={(e) => updateField('dmz', e.target.value)}
                        placeholder="DMZ configuration"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Interconnections Section */}
              {activeSection === 'interconnections' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-2">System Interconnections</h2>
                    <p className="text-slate-400 text-sm">
                      Outline the major connections to the system, how information is shared and backed up.
                    </p>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Data Flow Description</label>
                    <textarea
                      value={ssp?.dataFlowDescription || ''}
                      onChange={(e) => updateField('dataFlowDescription', e.target.value)}
                      rows={6}
                      placeholder="Describe the flow of data in and out of system boundaries..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Entry/Exit Protections</label>
                    <textarea
                      value={ssp?.entryExitProtections || ''}
                      onChange={(e) => updateField('entryExitProtections', e.target.value)}
                      rows={4}
                      placeholder="Describe protections implemented at all entry and exit points..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>
                </div>
              )}

              {/* Security Controls Section */}
              {activeSection === 'controls' && controlsData && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-2">Security Controls (NIST 800-171)</h2>
                    <p className="text-slate-400 text-sm">
                      Document how your organization implements each security control.
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="text-2xl font-bold text-green-400">{controlsData.stats?.implemented || 0}</div>
                      <div className="text-sm text-green-300">Implemented</div>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                      <div className="text-2xl font-bold text-yellow-400">{controlsData.stats?.partiallyImplemented || 0}</div>
                      <div className="text-sm text-yellow-300">Partial</div>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <div className="text-2xl font-bold text-blue-400">{controlsData.stats?.planned || 0}</div>
                      <div className="text-sm text-blue-300">Planned</div>
                    </div>
                    <div className="bg-slate-500/10 border border-slate-500/30 rounded-lg p-4">
                      <div className="text-2xl font-bold text-slate-400">{controlsData.stats?.notApplicable || 0}</div>
                      <div className="text-sm text-slate-300">N/A</div>
                    </div>
                  </div>

                  {/* Controls by Category */}
                  <div className="space-y-4">
                    {Object.entries(controlsData.groupedControls || {}).map(([category, controls]) => (
                      <div key={category} className="border border-slate-600 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                          className="w-full flex items-center justify-between p-4 bg-slate-700/50 hover:bg-slate-700 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Shield className="w-5 h-5 text-purple-400" />
                            <span className="font-medium text-white">{category}</span>
                            <span className="text-slate-400 text-sm">({controls.length} controls)</span>
                          </div>
                          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedCategory === category ? 'rotate-90' : ''}`} />
                        </button>
                        
                        {expandedCategory === category && (
                          <div className="p-4 space-y-4 bg-slate-800/50">
                            {controls.map((control) => (
                              <div key={control.controlId} className="border border-slate-600 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <span className="text-purple-400 font-mono text-sm">{control.controlId}</span>
                                    <h4 className="text-white font-medium">{control.controlTitle}</h4>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 mb-3">
                                  <div>
                                    <label className="block text-slate-400 text-xs mb-1">Status</label>
                                    <select
                                      value={control.implementationStatus || 'Not Applicable'}
                                      onChange={(e) => handleControlUpdate(control.controlId, 'implementationStatus', e.target.value)}
                                      className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    >
                                      <option value="Implemented">Implemented</option>
                                      <option value="Partially implemented">Partially Implemented</option>
                                      <option value="Planned">Planned</option>
                                      <option value="Alternative implementation">Alternative Implementation</option>
                                      <option value="Not Applicable">Not Applicable</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-slate-400 text-xs mb-1">Responsible Role</label>
                                    <input
                                      type="text"
                                      value={control.responsibleRole || ''}
                                      onChange={(e) => handleControlUpdate(control.controlId, 'responsibleRole', e.target.value)}
                                      placeholder="e.g., IT Security Manager"
                                      className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                  </div>
                                </div>
                                
                                <div>
                                  <label className="block text-slate-400 text-xs mb-1">Implementation Details</label>
                                  <textarea
                                    value={control.implementationDetails || ''}
                                    onChange={(e) => handleControlUpdate(control.controlId, 'implementationDetails', e.target.value)}
                                    placeholder="Describe how this control is implemented..."
                                    rows={2}
                                    className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Implementation Guidance Section */}
              {activeSection === 'guidance' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-2">Control Implementation Guidance</h2>
                    <p className="text-slate-400 text-sm">
                      Provide verbal description of how controls will be implemented and POA&M milestones.
                    </p>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Implementation Guidance</label>
                    <textarea
                      value={ssp?.implementationGuidance || ''}
                      onChange={(e) => updateField('implementationGuidance', e.target.value)}
                      rows={8}
                      placeholder="Describe how each control will be implemented. POA&M milestones should align with timeline and controls implementation..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">POA&M Milestones</label>
                    <textarea
                      value={ssp?.poamMilestones || ''}
                      onChange={(e) => updateField('poamMilestones', e.target.value)}
                      rows={6}
                      placeholder="List Plan of Action & Milestones..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Acronyms</label>
                    <textarea
                      value={ssp?.acronyms || ''}
                      onChange={(e) => updateField('acronyms', e.target.value)}
                      rows={4}
                      placeholder="List acronyms and their definitions used in this document..."
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-700">
                <button
                  onClick={goToPrevSection}
                  disabled={activeSection === SSP_SECTIONS[0].id}
                  className="flex items-center gap-2 px-4 py-2 text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                
                <button
                  onClick={goToNextSection}
                  disabled={activeSection === SSP_SECTIONS[SSP_SECTIONS.length - 1].id}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default SSPEditor;
